import { useState } from "react";
import { Check, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "../../lib/api";

const DAYS = { one_day: 1, one_week: 7, thirty_day: 30 };

export const SessionCalendar = ({ plan }) => {
  const total = DAYS[plan.program_length] || 7;
  const [done, setDone] = useState(plan.completed_days || []);
  const start = new Date(plan.created_at);
  const today = Math.floor((Date.now() - new Date(start.toDateString())) / 86400000) + 1;

  const toggle = async (day) => {
    const on = !done.includes(day);
    setDone((d) => (on ? [...d, day] : d.filter((x) => x !== day)));
    try {
      const { data } = await api.put(`/plans/${plan.id}/progress`, { day, done: on });
      setDone(data.completed_days);
    } catch (e) { toast.error(formatError(e)); }
  };
  const count = done.filter((d) => d <= total).length;
  const pct = Math.round((count / total) * 100);

  return (
    <section data-testid="session-calendar" className="mt-10 rounded-2xl bg-white border border-[#E4DFD4] p-6 sm:p-8 no-print">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl flex items-center gap-2"><CalendarCheck className="w-6 h-6 text-[#0D9488]" />Session calendar</h2>
          <p className="text-sm text-slate-600 mt-1">Tick off each day once you've run that day's sessions.</p>
        </div>
        <div className="text-right">
          <div data-testid="session-calendar-progress" className="font-mono text-2xl font-bold text-[#0F766E]">{count}/{total}</div>
          <div className="text-xs text-slate-500">days complete</div>
        </div>
      </div>
      <div className="mt-4 h-2 rounded-full bg-[#EFECE5] overflow-hidden"><div className="h-full bg-[#0D9488] transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
      <div className="mt-6 grid grid-cols-4 sm:grid-cols-7 gap-2">
        {Array.from({ length: total }, (_, i) => i + 1).map((day) => {
          const date = new Date(start);
          date.setDate(start.getDate() + day - 1);
          const isDone = done.includes(day);
          const isToday = day === today;
          return (
            <button key={day} type="button" data-testid={`calendar-day-${day}`} aria-pressed={isDone} onClick={() => toggle(day)}
              className={`relative rounded-xl border p-3 text-left transition-colors ${isDone ? "bg-[#0D9488] border-[#0D9488] text-white" : "bg-[#F8F7F4] border-[#E4DFD4] hover:border-[#0D9488]"} ${isToday && !isDone ? "ring-2 ring-[#0D9488]/40" : ""}`}>
              <div className={`text-[10px] font-mono uppercase ${isDone ? "text-white/80" : "text-slate-500"}`}>{date.toLocaleDateString(undefined, { weekday: "short" })}</div>
              <div className="font-display text-lg leading-tight">Day {day}</div>
              <div className={`text-[11px] ${isDone ? "text-white/80" : "text-slate-500"}`}>{date.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
              {isDone && <Check className="absolute top-2 right-2 w-4 h-4" />}
            </button>
          );
        })}
      </div>
      {count === total && <p data-testid="session-calendar-complete" className="mt-5 text-sm font-semibold text-[#0F766E]">Program complete — wonderful work! Ready for your next plan? Use "Re-assess" to build next month's program.</p>}
    </section>
  );
};
